import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const quickLinks = [
    { name: '인사말', href: '#인사말' },
    { name: '연혁', href: '#연혁' },
    { name: '조직도', href: '#조직도' },
    { name: '찾아오시는 길', href: '#찾아오시는 길' }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: 'fa-instagram', href: '#instagram' },
    { name: 'YouTube', icon: 'fa-youtube', href: '#youtube' },
    { name: 'Facebook', icon: 'fa-facebook', href: '#facebook' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4ta8i9t85rh3e48p370', {
        method: 'POST',
        body: new URLSearchParams({
          email: email
        }),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-800 to-gray-900 text-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute bottom-0 right-0 text-gray-700/20 font-bold text-[200px] leading-none pointer-events-none">
        UNI
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Column 1: Brand */}
          <div>
            <img 
              src="https://dtizen.net/img/uni_logo.png" 
              alt="유앤아이월드" 
              className="h-10 mb-6 brightness-0 invert"
            />
            <p className="text-gray-300 leading-relaxed">
              나눔과 봉사를 실천하는<br />
              <strong>유앤아이월드</strong>와 함께<br />
              더 나은 내일을 만들어갑니다
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">바로가기</h4>
            <ul className="space-y-4">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Social Media */}
          <div>
            <h4 className="text-lg font-bold mb-6">소셜미디어</h4>
            <ul className="space-y-4">
              {socialLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className={`fab ${link.icon} text-xl w-6 h-6 flex items-center justify-center`}></i>
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              유앤아이월드의 소식을<br />
              이메일로 받아보세요
            </p>
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소"
                  className="flex-1 bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  disabled={isSubmitting}
                  required
                />
                <button 
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="bg-white text-gray-900 w-12 h-12 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fas fa-arrow-right"></i>
                  )}
                </button>
              </div>
              
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <p className="text-green-400 text-sm">
                  구독이 완료되었습니다! 감사합니다.
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm">
                  올바른 이메일 주소를 입력해주세요.
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 mb-8"></div>

        {/* Bottom Info */}
        <div className="space-y-4 text-sm text-gray-400">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span><strong>주소:</strong> 서울특별시 마포구 만리재로 14 르네상스타워 1507호</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span><strong>전화:</strong> 1600-9817</span>
            <span><strong>팩스:</strong> 02-6338-4404</span>
            <span><strong>이메일:</strong> uniworld1507@gmail.com</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span><strong>대표자:</strong> 노상희</span>
            <span><strong>사업자번호:</strong> 449-82-00249</span>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-700 text-center text-sm text-gray-400">
          <p>Copyright &copy; 유앤아이월드 All rights reserved.</p>
          <div className="mt-4">
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 transition-colors text-xs cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}